import {
  CONTACT_PAGE_SIZE,
  CONTACT_ROLE_FILTER,
} from '../components/messaging-routes/contacts-handler';
import { MODEL_NAMES } from '../constants/index';
import { all, task, timeout } from 'ember-concurrency';
import {
  conversationInfinityModel,
  pageNumberInfinityOptions,
} from '../utils/messaging/infinity-model';
import { convertToUser } from '../utils/messaging/user';
import { findConversationByContacts } from '../utils/messaging/conversation';
import { isEmberTesting } from 'ember-simplepractice/utils/is-testing';
import { tracked } from '@glimmer/tracking';
import Service, { service } from '@ember/service';

const TOKEN_REFRESH_INTERVAL_MINS = 4.5;

const optionDefaults = {
  clientMessagingEnabled: false,
  groupMessagingEnabled: false,
  hasTeamMembers: false,
  messagingApiUrl: '',
  trackEvent: () => {},
};

export default class MessagingService extends Service {
  @service infinity;
  @service intl;
  @service messagingNavigation;
  @service('websocket/messagingWebsocket') messagingWebsocket;
  @service store;

  @tracked conversations;
  @tracked currentProfile;
  @tracked settingAwayStatus = false;

  options = { ...optionDefaults };
  token;

  get messagingApiUrl() {
    return this.options.messagingApiUrl;
  }

  get trackEvent() {
    return this.options.trackEvent;
  }

  get hasTeamMembers() {
    return this.options.hasTeamMembers;
  }

  get clientMessagingEnabled() {
    return this.options.clientMessagingEnabled;
  }

  get searchableContactsText() {
    let translationKey = 'emc.contacts_page.search_placeholder.team_members';

    if (this.clientMessagingEnabled) {
      translationKey = this.hasTeamMembers
        ? 'emc.contacts_page.search_placeholder.clients_contacts_and_team_members'
        : 'emc.contacts_page.search_placeholder.clients_and_contacts';
    }

    return this.intl.t(translationKey);
  }

  get groupMessagingEnabled() {
    return this.options.groupMessagingEnabled;
  }

  get tokenRefreshIntervalMins() {
    return isEmberTesting() ? 0 : TOKEN_REFRESH_INTERVAL_MINS;
  }

  loadApp = task({ drop: true }, async options => {
    this.options = { ...optionDefaults, ...options };
    await this.getMessagingToken.perform();
    await this.getCurrentProfile.perform();
    await this.getConversations.perform();

    if (this.currentProfile.isClient) {
      await this.populateVirtualConversations.perform();
      if (this.conversations.length === 1) {
        await this.messagingNavigation.toConversation(this.conversations.firstObject);
      }
    }

    this.pollMessagingToken.perform();
    this.messagingWebsocket.openSocket(this.messagingApiUrl, this.token);
  });

  getMessagingToken = task({ drop: true }, async () => {
    const response = await fetch('/messaging/tokens', {
      method: 'POST',
    });
    const json = await response.json();
    this.token = json.token;
  });

  getCurrentProfile = task({ drop: true }, async () => {
    this.currentProfile = await this.store.queryRecord(MODEL_NAMES.PROFILE, {
      include: 'system_user',
    });

    let user = this.store
      .peekAll(MODEL_NAMES.USER)
      .find(user => user.id === this.currentProfile.id);
    if (!user) {
      user = this.store.push({
        data: {
          id: this.currentProfile.id,
          type: MODEL_NAMES.USER,
          attributes: {
            name: this.currentProfile.name,
            externalScope: this.currentProfile.externalScope,
            externalId: this.currentProfile.externalId,
            organizationId: this.currentProfile.organizationId,
          },
        },
      });
    }
    this.currentProfile.user = user;
  });

  getConversations = task({ drop: true }, async () => {
    this.conversations = await this.infinity.model(
      MODEL_NAMES.CONVERSATION,
      {
        include: 'users,last_message',
        ...pageNumberInfinityOptions(),
      },
      conversationInfinityModel(this.currentProfile.isClient)
    );
  });

  populateVirtualConversations = task({ drop: true }, async () => {
    const contacts = await this.store.query(MODEL_NAMES.CONTACT, {
      filter: CONTACT_ROLE_FILTER,
      include: 'couple',
      page: { number: 1, size: CONTACT_PAGE_SIZE },
    });

    if (!contacts) {
      return;
    }

    const contactsWithoutConversations = contacts.filter(
      contact => !findConversationByContacts(this.conversations, contact.members)
    );

    const virtualConversations = contactsWithoutConversations.map(contact =>
      this.#buildVirtualConversation(contact.members)
    );
    this.conversations.unshiftObjects(virtualConversations);
  });

  findOrCreateConversationWithContacts(contacts) {
    const existingConversation = findConversationByContacts(this.conversations, contacts);

    if (existingConversation) {
      return existingConversation;
    }

    return this.#buildVirtualConversation(contacts);
  }

  async findOrCreateConversationByClientIds(clientExternalIds) {
    const contacts = await all(
      clientExternalIds.map(externalId =>
        this.store.adapterFor(MODEL_NAMES.CONTACT).fetchContact(externalId)
      )
    );

    return this.findOrCreateConversationWithContacts(contacts);
  }

  refreshProfileAndConversations = task({ drop: true }, async () => {
    return all([this.getCurrentProfile.perform(), this.getConversations.perform()]);
  });

  pollMessagingToken = task(async () => {
    do {
      await timeout(this.tokenRefreshIntervalMins * 60 * 1000);
      await this.getMessagingToken.perform();
    } while (!isEmberTesting());
  });

  #buildVirtualConversation = contacts => {
    const users = [this.currentProfile?.user, ...contacts.map(c => convertToUser(c, this.store))];
    return this.store.createRecord(MODEL_NAMES.CONVERSATION, {
      users,
    });
  };
}
